/* eslint-disable max-lines */
import { type ReferentPriceType, type USState } from '@strata/constants';
import { type CurrentBid } from '@strata/react-current-bid';
import Decimal from 'decimal.js';

import { AUCTION_API_URL, fmhNpiAPI } from 'api/APIBase';
import { type PerformanceCriteria } from 'services/AutomationRuleHelpers';

export async function postBid({
    npi,
    referralId,
    payerId,
    segmentId,
    bidAmount,
    referentPriceType,
}: {
    npi: string;
    referralId: string;
    payerId: string;
    segmentId: string;
    bidAmount: number;
    referentPriceType: ReferentPriceType;
}) {
    const url = `${AUCTION_API_URL}/`;

    const bidPayload = {
        referral_id: referralId,
        bid_amt: Decimal.div(bidAmount, 100).toNumber(),
        payer_id: payerId,
        segment_id: segmentId,
        maxbid_flag: false,
        automation_type_id: '0',
        referent_price_type: referentPriceType,
    };

    return fmhNpiAPI({ url, npi }).json(bidPayload).post().json<{
        winning_bid: string;
        state: typeof CurrentBid.states.WINNING | typeof CurrentBid.states.LOSING | 'tied';
    }>();
}

export async function postAccept({
    npi,
    referralId,
    payerId,
    segmentId,
    taskId,
}: {
    npi: string;
    referralId: string;
    payerId: string;
    segmentId: string;
    taskId?: string;
}) {
    const url = `${AUCTION_API_URL}/pending/accepted`;

    const payload = {
        referral_id: referralId,
        payer_id: payerId,
        segment_id: segmentId,
        task_id: taskId,
        supplier_npi: npi,
    };

    return fmhNpiAPI({ url, npi }).json(payload).post().json();
}

export async function postDecline({
    npi,
    referralId,
    payerId,
    segmentId,
    taskId,
    reason,
}: {
    npi: string;
    referralId: string;
    payerId: string;
    segmentId: string;
    taskId?: string;
    reason: string;
}) {
    const url = `${AUCTION_API_URL}/pending/declined`;

    const payload = {
        referral_id: referralId,
        payer_id: payerId,
        segment_id: segmentId,
        task_id: taskId,
        supplier_npi: npi,
        reason,
    };

    return fmhNpiAPI({ url, npi }).json(payload).post().json();
}

export interface AutomationRuleToDelete {
    bid_id: string;
    description: string;
}

/* Prescriber lists */

export interface PrescriberListPrescriber {
    npi: string;
    name: string;
}

export async function putPrescriberList({
    npi,
    prescriberListId,
    listName,
    prescribers,
}: {
    npi: string;
    prescriberListId?: string;
    listName: string;
    prescribers: PrescriberListPrescriber[];
}) {
    const url = `${AUCTION_API_URL}/prescriber_lists`;

    const payload = {
        prescriber_list_id: prescriberListId,
        prescriber_list_name: listName,
        prescriber_npis: prescribers,
    };

    return fmhNpiAPI({ url, npi }).put(payload).json();
}

export async function postDeletePrescriberList({
    npi,
    prescriberListId,
}: {
    npi: string;
    prescriberListId: string;
}) {
    const url = `${AUCTION_API_URL}/prescriber_lists/delete/${prescriberListId}`;

    return fmhNpiAPI({ url, npi }).post().json<AutomationRuleToDelete[]>();
}

/* State lists */

export async function putStateList({
    npi,
    stateListId,
    listName,
    states,
}: {
    npi: string;
    stateListId?: string;
    listName: string;
    states: USState[];
}) {
    const url = `${AUCTION_API_URL}/state_lists`;

    const payload = {
        state_list_id: stateListId,
        state_list_name: listName,
        states,
    };

    return fmhNpiAPI({ url, npi }).put(payload).json();
}

export async function postDeleteStateList({
    npi,
    stateListId,
}: {
    npi: string;
    stateListId: string;
}) {
    const url = `${AUCTION_API_URL}/state_lists/delete/${stateListId}`;

    return fmhNpiAPI({ url, npi }).post().json<AutomationRuleToDelete[]>();
}

/* Pre bid automation rules */

export async function getPreBids({ npi }: { npi: string }) {
    const url = `${AUCTION_API_URL}/prebids`;
    return fmhNpiAPI({ url, npi }).get().json<
        {
            bid_id: string;
            description: string;
            payer_id: string;
            segment_id: string;
            product_service_oid: string;
            is_brand: boolean;
            firstname?: string;
            member_id?: string;
            date_of_birth?: string;
            performance?: (typeof PerformanceCriteria)[keyof typeof PerformanceCriteria];
            place_of_service?: string;
            prescriber_list_id?: string;
            state_list_id?: string;
            username?: string;
            bid_amts: { bid_amt: number; referent_price_type: ReferentPriceType }[];
            is_active: boolean;
            run_once: boolean;
            start_date?: string;
            end_date?: string;
            active_through?: string;
        }[]
    >();
}

export async function postPreBid({
    npi,
    payerId,
    segmentId,
    productServiceOid,
    memberId,
    memberDOB,
    memberFirstName,
    performance,
    placeOfService,
    prescriberListId,
    stateListId,
    bidAmts,
    runOnce,
    startDate,
    endDate,
    description,
    isBrand,
}: {
    npi: string;
    payerId?: string;
    segmentId?: string;
    description: string;
    productServiceOid?: string;
    isBrand?: boolean;
    memberId?: string;
    memberDOB?: string;
    memberFirstName?: string;
    performance?: (typeof PerformanceCriteria)[keyof typeof PerformanceCriteria];
    placeOfService?: string;
    prescriberListId?: string;
    stateListId?: string;
    bidAmts?: { bidAmt: number; referentPriceType: ReferentPriceType }[];
    runOnce?: boolean;
    startDate?: string;
    endDate?: string;
}) {
    const url = `${AUCTION_API_URL}/prebids`;
    return fmhNpiAPI({ url, npi })
        .post({
            is_active: true,
            payer_id: payerId,
            segment_id: segmentId,
            product_service_oid: productServiceOid,
            member_id: memberId,
            date_of_birth: memberDOB,
            firstname: memberFirstName,
            performance,
            place_of_service: placeOfService,
            prescriber_list_id: prescriberListId,
            state_list_id: stateListId,
            bid_amts: bidAmts?.map((bidAmt) => ({
                bid_amt: bidAmt.bidAmt,
                referent_price_type: bidAmt.referentPriceType,
            })),
            run_once: runOnce,
            start_date: startDate,
            end_date: endDate,
            description,
            is_brand: isBrand,
        })
        .json<{ bid_id: string }>();
}

export async function putPreBid({
    npi,
    id,
    isActive,
    productServiceOid,
    memberId,
    memberDOB,
    memberFirstName,
    performance,
    placeOfService,
    prescriberListId,
    stateListId,
    bidAmts,
    runOnce,
    startDate,
    endDate,
    description,
    isBrand,
}: {
    npi: string;
    id: string;
    description: string;
    productServiceOid?: string;
    isBrand?: boolean;
    isActive?: boolean;
    memberId?: string;
    memberDOB?: string;
    memberFirstName?: string;
    performance?: (typeof PerformanceCriteria)[keyof typeof PerformanceCriteria];
    placeOfService?: string;
    prescriberListId?: string;
    stateListId?: string;
    bidAmts?: { bidAmt: number; referentPriceType: ReferentPriceType }[];
    runOnce?: boolean;
    startDate?: string;
    endDate?: string;
}) {
    const url = `${AUCTION_API_URL}/prebids/${id}`;
    return fmhNpiAPI({ url, npi })
        .put({
            is_active: isActive,
            product_service_oid: productServiceOid,
            member_id: memberId,
            date_of_birth: memberDOB,
            firstname: memberFirstName,
            performance,
            place_of_service: placeOfService,
            prescriber_list_id: prescriberListId,
            state_list_id: stateListId,
            bid_amts: bidAmts?.map((bidAmt) => ({
                bid_amt: bidAmt.bidAmt,
                referent_price_type: bidAmt.referentPriceType,
            })),
            run_once: runOnce,
            start_date: startDate,
            end_date: endDate,
            description,
            is_brand: isBrand,
        })
        .json();
}

export async function putTogglePreBid({ npi, id }: { npi: string; id: string }) {
    const url = `${AUCTION_API_URL}/prebids/${id}/toggle`;
    return fmhNpiAPI({ url, npi }).put().json();
}

export async function deletePreBid({ npi, id }: { npi: string; id: string }) {
    const url = `${AUCTION_API_URL}/prebids/${id}`;
    return fmhNpiAPI({ url, npi }).delete().json();
}
